/**
 * Shared drag-and-drop configuration for the GvG board.
 *
 * `<VueDraggable>` (from `vue-draggable-plus`) is bound per drop zone with
 * `v-model` pointing at a local writable ref, and `v-bind="dropZoneOptions()"`.
 * On `@end` the component reads its local ref and dispatches the matching
 * store action (`reorderGroup` / `reorderPool` / `movePlayerToGroup` / …).
 *
 * Keeping the shared `group` config here means every group plus each pool
 * bucket agrees on the group name, so a card can be dragged between the
 * currently-active pool bucket and any group in a single gesture. Switching
 * a card between the two pool buckets (Reserve ↔ Declined) is a two-step
 * gesture: switch the pool tab, then drag.
 *
 * The `dnd_ghost` / `dnd_chosen` / `dnd_drag` helper classes are bound here.
 * They intentionally use the `dnd_` domain prefix defined globally in
 * `src/styles/_mixins.scss` (an explicit AGENTS.md exception for SortableJS
 * helpers, since SortableJS applies them to the source/placeholder elements
 * outside any Vue `<style>` scope).
 */
import type { UseDraggableOptions } from 'vue-draggable-plus'

/** All GvG drop zones share this group name so cards move freely between them. */
export const GVG_DND_GROUP = 'gvg-players' as const

/**
 * Drop-zone items must carry an `id` field — SortableJS's `group` config
 * requires it for cross-list moves to work consistently. Callers may also
 * use a plain `string` (which is its own id) so `v-model` arrays like
 * `string[]` are accepted directly without wrapping each id in an object.
 */
export type DndItem = { readonly id: string } | string

/**
 * Build the SortableJS option set for a drop zone. Callers add their own
 * `onEnd` (and any per-zone overrides) by spreading this first.
 *
 * Auto-scroll is always on: when a card is dragged near the viewport edge
 * the page scrolls so the user can reach the whole board from a single drag.
 * Keep SortableJS on the browser's native HTML5 drag path. The pointer-driven
 * fallback can miss a mouse release when an interactive descendant handles
 * the event, leaving Sortable's internal state and fallback clone stuck until
 * the page is reloaded. Native dragend/drop events reliably terminate the
 * gesture, including when the pointer is released outside a group.
 *
 * `pull: true` — cards leave the source list when dragged out (rather than
 * being cloned). `put: true` accepts drops from any sibling zone that shares
 * this group. The boolean is the documented SortableJS shorthand for in-list
 * reordering; a future `'clone'` mode would switch `pull` to the string
 * `'clone'`.
 *
 * The `T extends DndItem` generic parameter exists for call-site ergonomics:
 * it types the returned `UseDraggableOptions<T>` so `v-model`'s element type
 * is checked end-to-end (`v-model="localIds: T[]"` etc.). The body itself
 * never references `T` because every option below is the same regardless of
 * the dragged item type — the shared `GVG_DND_GROUP` and helper classes are
 * domain-level, not per-item. Keeping the generic in the signature preserves
 * the inference chain without runtime cost.
 */
export function dropZoneOptions<T extends DndItem>(): UseDraggableOptions<T> {
  return {
    group: { name: GVG_DND_GROUP, pull: true, put: true },
    /**
     * Only player-card roots are sortable items. Empty groups keep a visible
     * non-interactive "Drop here" child, and SortableJS must not count that
     * placeholder as a list item: otherwise it bypasses its empty-zone
     * detection and refuses an incoming card. This is especially apparent in
     * vertical rooms, where an empty group has no neighbouring card midpoint
     * to use as an insertion target.
     */
    draggable: '[data-player-id]',
    animation: 150,
    ghostClass: 'dnd_ghost',
    chosenClass: 'dnd_chosen',
    dragClass: 'dnd_drag',
    disabled: false,
    scroll: true,
    scrollSensitivity: 60,
    bubbleScroll: true,
    forceFallback: false,
    handle: '[data-drag-handle]',
    filter: '[data-no-drag]',
    /**
     * Distance (in px) the pointer must be from an empty sortable — or from
     * the trailing edge of a list — to register an insert. Without this,
     * SortableJS only inserts relative to an existing item's midpoint, so a
     * drop released in the dead space between cards, below the last card,
     * or into an empty group is rejected and the card snaps back. The board's
     * groups carry a `min-h` + a `gap-2` between cards, both of which widen
     * that dead space; the threshold makes the whole zone a valid target.
     * SortableJS default is `0` (off).
     */
    emptyInsertThreshold: 8,
  }
}
